import TreeView from "@components/components/Tree";
import {ButtonVariant} from "@components/components/Tree/types";
import {compact, sortBy} from "lodash";
import {useTranslation} from "ni18n";
import React, {useCallback, useMemo} from "react";
import {useCurrentRoute} from "src/hooks/useCurrentRoute";

import {useTypedSelector} from "../../../store";
import {getRegionSlug} from "../../../store/getRegionSlug";
import {RegionSlug} from "../../../store/types";
import {generateRegionData} from "../../../utils/regionUtils";
import {getStateCodeFromRegionSlug} from "../../../utils/stateUtils";
import {TreeItem} from "../components/Tree/ComplexTreeWrapper";
import {
  sendAnalyticsDataOnRegionChange,
  sendAnalyticsDataOnSearch,
} from "./utils/handleSendAnalyticsData";
import {Url} from "next/dist/shared/lib/router/router";
import {LinkProps} from "next/link";
import {
  setUserSelectedLocation,
  useUserSelectedLocation,
} from "src/utils/browser-storage/userSelectedLocation";

interface Region {
  data: string;
  id: string;
}

const sfBayAreaRegion = {
  slug: getRegionSlug("sf-bay-area"),
  name: "Bay Area",
};

const isCARegion = (slug: string) => getStateCodeFromRegionSlug(slug) === "CA";

const buildRegionTree =
  (regions: Region[]) =>
  (searchTerm: string): TreeItem<string> => {
    const filteredRegions = regions.filter(({data}) =>
      data.toLowerCase().includes(searchTerm.toLowerCase()),
    );

    const filteredCARegions: TreeItem<string>[] = filteredRegions
      .filter(region => isCARegion(region.id))
      .map(region => ({
        id: region.id,
        data: region.data,
      }));

    const californiaParentItem: TreeItem<string> | undefined =
      filteredCARegions.length > 0
        ? {
            id: "ca",
            data: "California",
            children: filteredCARegions,
          }
        : undefined;

    const otherRegions = filteredRegions.filter(({id}) => !isCARegion(id));

    return {
      id: "root",
      children: sortBy(
        compact([
          californiaParentItem,
          ...otherRegions.map(region => ({
            id: region.id,
            data: region.data,
          })),
        ]),
        "data",
      ),
      data: "Root",
    };
  };

interface RegionTreeSelector {
  buttonVariant?: ButtonVariant;
  initialRegionSlug?: string;
  onSelect?: (slug: RegionSlug) => void;
  hideVirtualOnly?: boolean;
  inClinicSpecialtyIds?: string[];
  getHref?: (item: TreeItem<string>) => Url | undefined;
  getAs?: (item: TreeItem<string>) => LinkProps["as"] | undefined;
  shallow?: LinkProps["shallow"];
}

const RegionTreeSelector: React.FC<RegionTreeSelector> = ({
  buttonVariant = "primary",
  onSelect,
  initialRegionSlug,
  inClinicSpecialtyIds,
  hideVirtualOnly = false,
  getHref,
  getAs,
  shallow,
}) => {
  const i18n = useTranslation();
  const {regionSlug: selectedFromLocalStorage = "sf-bay-area"} = useUserSelectedLocation();
  const {locations} = useTypedSelector(({config}) => config);
  const closestRegion = locations[0]?.region || sfBayAreaRegion;

  const selectedRegion = initialRegionSlug ?? selectedFromLocalStorage;
  const currentRoute = useCurrentRoute();

  const selectorName = i18n.t("Region");
  const searchLabel = i18n.t("Search by region name");
  const searchPlaceholder = i18n.t("Search regions");

  const regions = useMemo(
    () => generateRegionData(locations, inClinicSpecialtyIds, hideVirtualOnly),
    [hideVirtualOnly, inClinicSpecialtyIds, locations],
  );

  const expandedItems = useMemo(
    () => (getStateCodeFromRegionSlug(selectedFromLocalStorage) === "CA" ? ["ca"] : []),
    [selectedFromLocalStorage],
  );

  const items = useMemo(() => regions.map(({slug, name}) => ({data: name, id: slug})), [regions]);

  const selected = useMemo(
    () =>
      items.find(({id}) => id === selectedRegion) || {
        id: closestRegion.slug,
        data: closestRegion.name,
      },
    [closestRegion.name, closestRegion.slug, items, selectedRegion],
  );

  const handleSelect = useCallback(
    (slug: string | number) => {
      if (typeof slug === "string") {
        if (selectedFromLocalStorage !== slug)
          setUserSelectedLocation({regionSlug: slug as RegionSlug});
        if (onSelect) onSelect(slug as RegionSlug);
        sendAnalyticsDataOnRegionChange(slug, currentRoute);
      }
    },
    [currentRoute, onSelect, selectedFromLocalStorage],
  );

  const buildTree = useMemo(() => buildRegionTree(items), [items]);

  return (
    <TreeView
      buttonVariant={buttonVariant}
      selectorName={selectorName}
      searchLabel={searchLabel}
      searchPlaceholder={searchPlaceholder}
      selected={selected}
      initialExpandedItems={expandedItems}
      handleSelect={handleSelect}
      handleSearch={sendAnalyticsDataOnSearch}
      buildTree={buildTree}
      getHref={getHref}
      getAs={getAs}
      shallow={shallow}
    />
  );
};

export default React.memo(RegionTreeSelector);
